import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
import { agenciesAPI, uploadAPI } from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Loading from '../common/Loading';
import { handleApiError, getImageUrl } from '../../utils/helpers';

const AgencySettingsPage = () => {
  const { isAdmin } = useAuth();
  const { isRTL } = useLanguage();
  const { hasWhiteLabeling } = useWhiteLabel();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [agency, setAgency] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      fetchAgency();
    }
  }, [isAdmin]);

  const fetchAgency = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agenciesAPI.getMyAgency();
      const agencyData = response.data.agency;
      setAgency(agencyData);
      setFormData({
        name: agencyData.name || '',
        logo_url: agencyData.logo_url || '',
      });
      if (agencyData.logo_url) {
        setLogoPreview(getImageUrl(agencyData.logo_url));
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
    setSuccess(null);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(isRTL ? 'الملف يجب أن يكون صورة' : 'File must be an image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(isRTL ? 'حجم الملف يجب أن يكون أقل من 5 ميجابايت' : 'File size must be less than 5MB');
      return;
    }

    setLogoFile(file);
    setError(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      let logoUrl = formData.logo_url;

      if (logoFile) {
        const uploadResponse = await uploadAPI.uploadImage(logoFile);
        logoUrl = uploadResponse.data.url;
      }

      const updateData = {
        name: formData.name.trim(),
        logo_url: logoUrl || null,
      };

      await agenciesAPI.update(agency.id, updateData);
      
      setSuccess(isRTL ? 'تم تحديث الإعدادات بنجاح' : 'Settings updated successfully');
      await fetchAgency();
      setLogoFile(null);
      
      // Dispatch event to update sidebar and white label context
      window.dispatchEvent(new CustomEvent('agencyUpdated'));
      window.dispatchEvent(new CustomEvent('refreshWhiteLabel'));
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">
          {isRTL ? 'غير مصرح لك بالوصول إلى هذه الصفحة' : 'You do not have access to this page'}
        </p>
      </div>
    );
  }

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="overflow-hidden border-0 shadow-sm">
        <div className="bg-gradient-to-r from-sky-50 via-white to-emerald-50 px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-start gap-3 sm:gap-4">
            <Link
              to="/settings"
              className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/80 text-slate-700 hover:bg-white shadow-sm border border-slate-200 transition-colors flex-shrink-0"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                {isRTL ? 'إعدادات الوكالة' : 'Agency Settings'}
              </p>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
                {isRTL ? 'معلومات الوكالة' : 'Agency Information'}
              </h1>
              <p className="text-slate-600 text-sm sm:text-base mt-1">
                {isRTL
                  ? 'حدّث اسم الوكالة والشعار، وتابع تفاصيل الباقة والحدود المتبقية.'
                  : 'Update your agency name and logo, and review your plan details and remaining limits.'}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Agency Information Form */}
      <Card className="p-6 border border-slate-200 rounded-2xl bg-white">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {/* Agency Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'اسم الوكالة' : 'Agency Name'} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder={isRTL ? 'مثال: وكالة الإبداع' : 'e.g., Creative Agency'}
              className="input w-full"
            />
          </div>

          {/* Agency Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'شعار الوكالة' : 'Agency Logo'}
            </label>
            
            {logoPreview && (
              <div className="mb-4">
                <div className="inline-block relative">
                  <img
                    src={logoPreview}
                    alt={isRTL ? 'معاينة الشعار' : 'Logo preview'}
                    className="w-32 h-32 object-contain border-2 border-gray-200 rounded-lg bg-gray-50 p-2"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center gap-4">
              <label className="cursor-pointer">
                <span className="inline-block px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors">
                  {logoFile 
                    ? (isRTL ? 'تغيير الصورة' : 'Change Image')
                    : (logoPreview 
                        ? (isRTL ? 'تغيير الشعار' : 'Change Logo')
                        : (isRTL ? 'اختر الشعار' : 'Choose Logo')
                      )
                  }
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />
              </label>
              
              {logoPreview && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setLogoPreview(null);
                    setLogoFile(null);
                    setFormData(prev => ({ ...prev, logo_url: '' }));
                  }}
                >
                  {isRTL ? 'إزالة' : 'Remove'}
                </Button>
              )}
            </div>

            <p className="text-xs text-gray-500 mt-2">
              {isRTL 
                ? 'الصيغ المدعومة: JPG, PNG, GIF. الحد الأقصى للحجم: 5 ميجابايت'
                : 'Supported formats: JPG, PNG, GIF. Max size: 5MB'}
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end pt-4 border-t border-gray-100">
            <Button
              type="submit"
              variant="primary"
              loading={saving}
              disabled={saving}
            >
              {isRTL ? 'حفظ الإعدادات' : 'Save Settings'}
            </Button>
          </div>
        </form>
      </Card>

      {/* Plan Details Card */}
      {agency && (
        <Card className="p-6 border border-slate-200 rounded-2xl bg-white">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {isRTL ? 'تفاصيل الباقة' : 'Plan Details'}
          </h2>
          
          {/* Plan Name and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">{isRTL ? 'الباقة' : 'Plan Name'}</p>
              <p className="text-lg font-semibold text-gray-900">{agency.plan_name || (isRTL ? 'غير محدد' : 'Not set')}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">{isRTL ? 'حالة الاشتراك' : 'Subscription Status'}</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                agency.subscription_status === 'active' 
                  ? 'bg-green-100 text-green-800'
                  : agency.subscription_status === 'trial'
                  ? 'bg-blue-100 text-blue-800'
                  : agency.subscription_status === 'expired'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {agency.subscription_status === 'active' 
                  ? (isRTL ? 'نشط' : 'Active')
                  : agency.subscription_status === 'trial'
                  ? (isRTL ? 'تجريبي' : 'Trial')
                  : agency.subscription_status === 'expired'
                  ? (isRTL ? 'منتهي' : 'Expired')
                  : (agency.subscription_status || (isRTL ? 'غير محدد' : 'Not set'))
                }
              </span>
            </div>
          </div>

          {/* Plan Limits */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'حدود الباقة' : 'Plan Limits'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{isRTL ? 'الحد الأقصى للبوستات' : 'Max Posts'}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {agency.max_posts !== null && agency.max_posts !== undefined 
                    ? agency.max_posts 
                    : (isRTL ? '∞' : '∞')}
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{isRTL ? 'الحد الأقصى للعملاء' : 'Max Clients'}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {agency.max_clients !== null && agency.max_clients !== undefined 
                    ? agency.max_clients 
                    : (isRTL ? '∞' : '∞')}
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{isRTL ? 'الحد الأقصى للمستخدمين' : 'Max Users'}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {agency.max_users !== null && agency.max_users !== undefined 
                    ? agency.max_users 
                    : (isRTL ? '∞' : '∞')}
                </p>
              </div>
            </div>
          </div>

          {/* Remaining Counts */}
          {(agency.remaining_posts !== null || agency.remaining_users !== null || agency.remaining_clients !== null) && (
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {isRTL ? 'المتبقي' : 'Remaining'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {agency.remaining_posts !== null && (
                  <div className={`p-4 rounded-lg ${
                    agency.remaining_posts === 0 
                      ? 'bg-red-50 border border-red-200' 
                      : agency.remaining_posts <= 5 
                      ? 'bg-amber-50 border border-amber-200' 
                      : 'bg-emerald-50 border border-emerald-200'
                  }`}>
                    <p className="text-sm text-gray-600 mb-1">{isRTL ? 'البوستات المتبقية' : 'Remaining Posts'}</p>
                    <p className={`text-2xl font-bold ${
                      agency.remaining_posts === 0 
                        ? 'text-red-700' 
                        : agency.remaining_posts <= 5 
                        ? 'text-amber-700' 
                        : 'text-emerald-700'
                    }`}>
                      {agency.remaining_posts}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {isRTL 
                        ? `من أصل ${agency.max_posts} (مستخدم: ${agency.current_posts || 0})`
                        : `of ${agency.max_posts} (used: ${agency.current_posts || 0})`}
                    </p>
                  </div>
                )}
                {agency.remaining_clients !== null && (
                  <div className={`p-4 rounded-lg ${
                    agency.remaining_clients === 0 
                      ? 'bg-red-50 border border-red-200' 
                      : agency.remaining_clients <= 2 
                      ? 'bg-amber-50 border border-amber-200' 
                      : 'bg-emerald-50 border border-emerald-200'
                  }`}>
                    <p className="text-sm text-gray-600 mb-1">{isRTL ? 'العملاء المتبقين' : 'Remaining Clients'}</p>
                    <p className={`text-2xl font-bold ${
                      agency.remaining_clients === 0 
                        ? 'text-red-700' 
                        : agency.remaining_clients <= 2 
                        ? 'text-amber-700' 
                        : 'text-emerald-700'
                    }`}>
                      {agency.remaining_clients}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {isRTL 
                        ? `من أصل ${agency.max_clients} (مستخدم: ${agency.current_clients || 0})`
                        : `of ${agency.max_clients} (used: ${agency.current_clients || 0})`}
                    </p>
                  </div>
                )}
                {agency.remaining_users !== null && (
                  <div className={`p-4 rounded-lg ${
                    agency.remaining_users === 0 
                      ? 'bg-red-50 border border-red-200' 
                      : agency.remaining_users <= 2 
                      ? 'bg-amber-50 border border-amber-200' 
                      : 'bg-emerald-50 border border-emerald-200'
                  }`}>
                    <p className="text-sm text-gray-600 mb-1">{isRTL ? 'المستخدمين المتبقين' : 'Remaining Users'}</p>
                    <p className={`text-2xl font-bold ${
                      agency.remaining_users === 0 
                        ? 'text-red-700' 
                        : agency.remaining_users <= 2 
                        ? 'text-amber-700' 
                        : 'text-emerald-700'
                    }`}>
                      {agency.remaining_users}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {isRTL 
                        ? `من أصل ${agency.max_users} (مستخدم: ${agency.current_users || 0})`
                        : `of ${agency.max_users} (used: ${agency.current_users || 0})`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Plan Features */}
          {agency.plan_features && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {isRTL ? 'المميزات المتاحة' : 'Available Features'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const features = Array.isArray(agency.plan_features) 
                    ? agency.plan_features 
                    : (typeof agency.plan_features === 'string' 
                        ? JSON.parse(agency.plan_features) 
                        : []);
                  
                  return features.map((feature, index) => {
                    const featureName = typeof feature === 'string' ? feature : (feature.name || feature);
                    const featureNameAr = typeof feature === 'object' ? feature.name_ar : null;
                    return (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-sky-100 text-sky-800"
                      >
                        {isRTL && featureNameAr ? featureNameAr : featureName}
                      </span>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* White-labeling Notice */}
          {!hasWhiteLabeling && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                {isRTL 
                  ? '⚠️ ميزة White-labeling غير متاحة في باقاتك الحالية. قم بالترقية للوصول إلى التخصيص الكامل (اسم، شعار، رابط مخصص).'
                  : '⚠️ White-labeling feature is not available in your current plan. Upgrade to access full customization (name, logo, custom URL).'}
              </p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default AgencySettingsPage;


